import React from "react";
import { logUsage } from "../api/logUsage";
import { checkMaxEventsPerSessionCount } from "owa-analytics-utils/lib/checkMaxEventsPerSessionCount";
import type { AnalyticsEventNames } from "owa-analytics-events";
import type { MailboxInfo } from "owa-client-types";
import type { CustomData } from "owa-analytics-types";

export interface RenderStatsOptions {
    /** Maximum number of events to actually log per session. Defaults to 10. */
    maxEvents?: number;
    /** Percentage of sessions that log the render statistics event. */
    sessionSampleRate?: number;
    /** Additional custom data to include in the telemetry event. */
    customData?: CustomData;
}

/**
 * Hook that tracks render count and average render time for a component,
 * logging aggregated stats via logUsage when the component unmounts or
 * the deps change.
 *
 * Render time is measured from the start of the render (when the hook is
 * called) through commit and layout effects, captured via useLayoutEffect. This
 * includes React reconciliation, DOM mutations, and layout effect execution,
 * giving a more complete picture of the real cost of each render.
 *
 * Must be called at the top of the component function body (before any
 * early returns) so that every render is counted.
 *
 * @param eventName - The telemetry event name to log.
 * @param mailboxInfo - MailboxInfo for multi-account telemetry routing.
 * @param component - Short identifier for the component (e.g. 'IH', 'EIP', 'CRP').
 * @param deps - Dependency array that controls when stats are flushed and
 *               reset (e.g. `[item?.ItemId?.Id]`). An empty array means
 *               stats are only flushed on unmount.
 * @param options - Optional configuration for the render stats telemetry.
 */
export function useRenderStats(eventName: AnalyticsEventNames, mailboxInfo: MailboxInfo, component: string, deps: React.DependencyList, options?: RenderStatsOptions): void {
    const { maxEvents = 10, sessionSampleRate, customData } = options ?? {};
    
    const renderCount = React.useRef<number>(0);
    const totalRenderTime = React.useRef<number>(0);
    
    // Capture render start time in a local const so the layout effect closes
    // over the correct value even if React interrupts and restarts the render
    // (concurrent mode). A mutable ref would be overwritten by the retry.
    const renderStart = performance.now();
    
    // Capture end time after commit — includes reconciliation, DOM mutations,
    // and layout effects
    React.useLayoutEffect(() => {
        renderCount.current++;
        totalRenderTime.current += performance.now() - renderStart;
    });
    
    // Flush stats on unmount or when deps change
    // Choose deps wisely based on when you want to reset the stats.
    // For example, if you want to track stats per item, you might use [item?.ItemId?.Id]. If you want to track across the entire component lifecycle, use [].
    React.useEffect(() => {
        return () => {
            const sessionCountKey = `${eventName}_${component}`;
            if (checkMaxEventsPerSessionCount(sessionCountKey, maxEvents)) {
                logUsage(eventName, mailboxInfo, {
                    renderCount: renderCount.current,
                    avgRenderTime: totalRenderTime.current / renderCount.current,
                    component,
                    ...customData,
                }, sessionSampleRate === undefined ? undefined : { sessionSampleRate });
            }
            renderCount.current = 0;
            totalRenderTime.current = 0;
        };
    }, deps);
}
